import posthog from 'posthog-js';

/**
 * Error types for frontend error tracking
 */
export type ClientErrorType =
  | 'api_error'
  | 'render_error'
  | 'network_error'
  | 'validation_error'
  | 'auth_error'
  | 'unknown_error';

interface ErrorContext {
  component?: string;
  action?: string;
  resourceId?: string;
  [key: string]: unknown;
}

/**
 * Track a custom event in PostHog
 * @param event - Event name (use snake_case, e.g., 'button_clicked')
 * @param properties - Additional event properties
 */
export function trackEvent(event: string, properties?: Record<string, unknown>) {
  posthog.capture(event, properties);
}

/**
 * Track an error event in PostHog
 * @param error - The error object or message
 * @param errorType - Category of error
 * @param context - Additional context (component, action, etc.)
 */
export function trackError(
  error: Error | string,
  errorType: ClientErrorType,
  context: ErrorContext = {}
) {
  const errorMessage = error instanceof Error ? error.message : error;
  const errorStack = error instanceof Error ? error.stack : undefined;

  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    error_stack: errorStack,
    component: context.component,
    action: context.action,
    resource_id: context.resourceId,
    ...context,
  });

  // Also log to console in development
  if (import.meta.env.DEV) {
    console.error(`[${errorType}] ${errorMessage}`, { context, error });
  }
}

/**
 * Identify the current user
 * @param userId - Unique user identifier
 * @param properties - User properties to set
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  posthog.identify(userId, properties);
}

/**
 * Reset the current user (on logout)
 */
export function resetUser() {
  posthog.reset();
}

/**
 * Set user properties without identifying
 * @param properties - Properties to set on the current user
 */
export function setUserProperties(properties: Record<string, unknown>) {
  posthog.people.set(properties);
}

// Pre-defined event helpers for common actions
export const analytics = {
  // Landing page events
  landingPageViewed: () => trackEvent('landing_page_viewed', { source: 'organic' }),

  landingCtaClicked: (location: 'header' | 'hero') =>
    trackEvent('landing_cta_clicked', { location }),

  landingGithubClicked: (location: 'hero' | 'own_your_data' | 'footer') =>
    trackEvent('landing_github_clicked', { location }),

  landingDockerSnippetCopied: () => trackEvent('landing_docker_snippet_copied', {}),

  // Navigation events
  pageViewed: (pageName: string, properties?: Record<string, unknown>) =>
    trackEvent('page_viewed', { page_name: pageName, ...properties }),

  // Calendar events
  calendarSourceAdded: (sourceType: string, memberId: string) =>
    trackEvent('calendar_source_added', { source_type: sourceType, member_id: memberId }),

  calendarSynced: (sourceId: string) =>
    trackEvent('calendar_synced', { source_id: sourceId }),

  calendarEventViewed: (eventId: string, viewMode: string) =>
    trackEvent('calendar_event_viewed', { event_id: eventId, view_mode: viewMode }),

  calendarEventModalClosed: (eventId: string, viewDurationMs?: number) =>
    trackEvent('calendar_event_modal_closed', { event_id: eventId, view_duration_ms: viewDurationMs }),

  // Shared event deduplication (events appearing on multiple family members' calendars)
  sharedEventDisplayed: (eventId: string, memberCount: number, viewContext: string) =>
    trackEvent('shared_event_displayed', {
      event_id: eventId,
      member_count: memberCount,
      view_context: viewContext,
    }),

  // Upcoming events (Home page 4th column)
  upcomingEventsShown: (eventCount: number) =>
    trackEvent('upcoming_events_shown', { event_count: eventCount }),

  upcomingEventClicked: (eventId: string, daysAhead: number) =>
    trackEvent('upcoming_event_clicked', { event_id: eventId, days_ahead: daysAhead }),

  // Dynamic time range (Home page daily view)
  dynamicTimeRangeCalculated: (
    startHour: number,
    endHour: number,
    eventCount: number,
    hasAllDayEvents: boolean
  ) =>
    trackEvent('dynamic_time_range_calculated', {
      start_hour: startHour,
      end_hour: endHour,
      event_count: eventCount,
      has_all_day_events: hasAllDayEvents,
      window_hours: endHour - startHour,
    }),

  emptyDayViewed: (hasAllDayEvents: boolean) =>
    trackEvent('empty_day_viewed', { has_all_day_events: hasAllDayEvents }),

  // Event density (adaptive display)
  eventDensityCalculated: (
    viewMode: string,
    density: 'spacious' | 'normal' | 'compact',
    eventCount: number,
    memberId?: string
  ) =>
    trackEvent('event_density_calculated', {
      view_mode: viewMode,
      density,
      event_count: eventCount,
      member_id: memberId,
    }),

  // Chore events
  choreCreated: (choreId: string, recurrence: string) =>
    trackEvent('chore_created', { chore_id: choreId, recurrence }),

  choreCompleted: (choreId: string, memberId: string, points: number) =>
    trackEvent('chore_completed', { chore_id: choreId, member_id: memberId, points_earned: points }),

  // List events
  listCreated: (listId: string, listType: string) =>
    trackEvent('list_created', { list_id: listId, list_type: listType }),

  listItemAdded: (listId: string) =>
    trackEvent('list_item_added', { list_id: listId }),

  listItemChecked: (listId: string, itemId: string) =>
    trackEvent('list_item_checked', { list_id: listId, item_id: itemId }),

  // Family events
  memberAdded: (memberId: string, memberType: string) =>
    trackEvent('member_added', { member_id: memberId, member_type: memberType }),

  // Connected accounts and calendar button interactions
  connectedAccountsViewed: (memberId: string, hasGoogle: boolean, hasMicrosoft: boolean) =>
    trackEvent('connected_accounts_viewed', {
      member_id: memberId,
      has_google: hasGoogle,
      has_microsoft: hasMicrosoft,
    }),

  calendarButtonDisabledViewed: (memberId: string, provider: 'google' | 'microsoft') =>
    trackEvent('calendar_button_disabled_viewed', {
      member_id: memberId,
      provider,
      reason: 'no_account_connected',
    }),

  inviteTriggeredFromDisabledButton: (memberId: string, provider: 'google' | 'microsoft') =>
    trackEvent('invite_triggered_from_disabled_button', {
      member_id: memberId,
      provider,
    }),

  // Feature usage
  featureUsed: (featureName: string, properties?: Record<string, unknown>) =>
    trackEvent('feature_used', { feature_name: featureName, ...properties }),

  // Google Keep events
  keepConnectModalOpened: () =>
    trackEvent('keep_connect_modal_opened'),

  keepListsModalOpened: () =>
    trackEvent('keep_lists_modal_opened'),

  keepListImported: (keepListId: string) =>
    trackEvent('keep_list_imported', { keep_list_id: keepListId }),

  keepSyncTriggered: (sourceId: string) =>
    trackEvent('keep_sync_triggered', { source_id: sourceId }),

  // Notification events
  pushNotificationsEnabled: () =>
    trackEvent('push_notifications_enabled'),

  pushNotificationsDisabled: () =>
    trackEvent('push_notifications_disabled'),

  notificationSettingsChanged: (setting: string, value: unknown) =>
    trackEvent('notification_settings_changed', { setting, value }),

  testNotificationSent: () =>
    trackEvent('test_notification_sent'),

  // Error tracking helpers
  apiError: (error: Error | string, action: string, component?: string) =>
    trackError(error, 'api_error', { action, component }),

  networkError: (error: Error | string, action: string, component?: string) =>
    trackError(error, 'network_error', { action, component }),

  renderError: (error: Error | string, component: string) =>
    trackError(error, 'render_error', { component }),

  authError: (error: Error | string, action: string) =>
    trackError(error, 'auth_error', { action }),

  // Sidebar events
  sidebarToggled: (isExpanded: boolean) =>
    trackEvent('sidebar_toggled', { is_expanded: isExpanded }),

  miniCalendarDateClicked: (date: string, isPast?: boolean) =>
    trackEvent('mini_calendar_date_clicked', { date, is_past: isPast }),

  miniCalendarMonthChanged: (direction: 'prev' | 'next', newMonth: string) =>
    trackEvent('mini_calendar_month_changed', { direction, new_month: newMonth }),

  sidebarChoreCompleted: (choreId: string, memberId: string, points: number) =>
    trackEvent('sidebar_chore_completed', {
      chore_id: choreId,
      member_id: memberId,
      points_earned: points,
      source: 'sidebar',
    }),

  // Shared chore popover events (legacy - replaced by modal)
  sharedChorePopoverOpened: (choreId: string, memberCount: number) =>
    trackEvent('shared_chore_popover_opened', {
      chore_id: choreId,
      member_count: memberCount,
      source: 'sidebar',
    }),

  sharedChorePopoverClosed: (choreId: string, selectionMade: boolean) =>
    trackEvent('shared_chore_popover_closed', {
      chore_id: choreId,
      selection_made: selectionMade,
      source: 'sidebar',
    }),

  // Chore completion modal events
  choreCompletionModalOpened: (
    choreId: string,
    source: 'sidebar' | 'chores_page',
    memberCount: number
  ) =>
    trackEvent('chore_completion_modal_opened', {
      chore_id: choreId,
      source,
      member_count: memberCount,
    }),

  choreCompletionModalClosed: (choreId: string, selectionMade: boolean) =>
    trackEvent('chore_completion_modal_closed', {
      chore_id: choreId,
      selection_made: selectionMade,
    }),

  choreCompletionCelebrated: (choreId: string, memberId: string, points: number) =>
    trackEvent('chore_completion_celebrated', {
      chore_id: choreId,
      member_id: memberId,
      points_earned: points,
    }),

  // Scroll indicator events (off-screen calendar events)
  scrollIndicatorShown: (position: 'top' | 'bottom', hiddenCount: number) =>
    trackEvent('scroll_indicator_shown', {
      position,
      hidden_event_count: hiddenCount,
      view_mode: 'home',
    }),

  scrollIndicatorClicked: (position: 'top' | 'bottom', hiddenCount: number) =>
    trackEvent('scroll_indicator_clicked', {
      position,
      hidden_event_count: hiddenCount,
      view_mode: 'home',
    }),

  // Key event preview (Home page day selector cards)
  keyEventShown: (dayOffset: number, isAllDay: boolean, memberId: string | null) =>
    trackEvent('key_event_shown', {
      day_offset: dayOffset,
      is_all_day: isAllDay,
      member_id: memberId,
    }),

  keyEventDaySelected: (dayOffset: number, hasKeyEvent: boolean) =>
    trackEvent('key_event_day_selected', {
      day_offset: dayOffset,
      has_key_event: hasKeyEvent,
    }),

  // Multi-event day preview (Home page 3-day view)
  dayPreviewEventsShown: (
    dayOffset: number,
    eventsShown: number,
    totalEvents: number,
    remainingCount: number
  ) =>
    trackEvent('day_preview_events_shown', {
      day_offset: dayOffset,
      events_shown: eventsShown,
      total_events: totalEvents,
      remaining_count: remainingCount,
      has_overflow: remainingCount > 0,
    }),

  // Overview section collapse/expand (Home page 3-day preview)
  overviewToggled: (isCompact: boolean) =>
    trackEvent('overview_toggled', { is_compact: isCompact, page: 'home' }),

  // Child/Teen search keyword events (for event re-attribution)
  childKeywordAdded: (memberId: string, keyword: string) =>
    trackEvent('child_keyword_added', { member_id: memberId, keyword }),

  childKeywordRemoved: (memberId: string, keyword: string) =>
    trackEvent('child_keyword_removed', { member_id: memberId, keyword }),

  // Clock tile events (sidebar time display)
  clockTileViewed: (timeFormat: string, timestamp: string) =>
    trackEvent('clock_tile_viewed', { time_format: timeFormat, timestamp }),

  clockTileClicked: (
    timeFormat: string,
    upcomingEventsCount: number,
    hasImminentEvent: boolean
  ) =>
    trackEvent('clock_tile_clicked', {
      time_format: timeFormat,
      upcoming_events_count: upcomingEventsCount,
      has_imminent_event: hasImminentEvent,
    }),

  clockTileEventClicked: (eventId: string, memberId: string | null) =>
    trackEvent('clock_tile_event_clicked', { event_id: eventId, member_id: memberId }),

  // School checklist events
  schoolViewActivated: () =>
    trackEvent('school_view_activated'),

  schoolChecklistItemChecked: (memberId: string, itemKey: string, source: 'base' | 'override' | 'keyword') =>
    trackEvent('school_checklist_item_checked', {
      member_id: memberId,
      item_key: itemKey,
      source,
    }),

  schoolChecklistItemUnchecked: (memberId: string, itemKey: string) =>
    trackEvent('school_checklist_item_unchecked', {
      member_id: memberId,
      item_key: itemKey,
    }),

  schoolChecklistItemCreated: (itemId: string, category: string | null) =>
    trackEvent('school_checklist_item_created', {
      item_id: itemId,
      category,
    }),

  schoolKeywordRuleCreated: (ruleId: string, keyword: string) =>
    trackEvent('school_keyword_rule_created', {
      rule_id: ruleId,
      keyword,
    }),
};
